// Firestore + auth helpers for the Abacus tool.
// Everything lives under /users/{uid}/abacus/profile, which the existing
// Firestore rules already allow the owner to read/write.
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export const HISTORY_LIMIT = 25;

export function abacusDoc(uid) {
  return doc(db, "users", uid, "abacus", "profile");
}

export async function loadAbacusProfile(uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(abacusDoc(uid));
    return snap.exists() ? snap.data() : null;
  } catch (_) {
    return null;
  }
}

export async function saveAbacusSettings(uid, settings) {
  if (!uid) return;
  try {
    await setDoc(
      abacusDoc(uid),
      { settings, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (_) {
    /* best-effort */
  }
}

export function emptyStats() {
  return {
    testsTaken: 0,
    totalQuestions: 0,
    totalCorrect: 0,
    bestScorePct: 0,
    bestStreak: 0,
  };
}

// Fold a finished test into the running stats + capped history list.
export function foldTestResult(prevStats, prevHistory, result) {
  const stats = { ...emptyStats(), ...(prevStats || {}) };
  const scorePct = result.total > 0
    ? Math.round((result.correct / result.total) * 100)
    : 0;

  const nextStats = {
    testsTaken: stats.testsTaken + 1,
    totalQuestions: stats.totalQuestions + result.total,
    totalCorrect: stats.totalCorrect + result.correct,
    bestScorePct: Math.max(stats.bestScorePct, scorePct),
    bestStreak: Math.max(stats.bestStreak, result.bestStreak || 0),
  };

  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    at: Date.now(),
    mode: result.mode,
    label: result.label,
    total: result.total,
    correct: result.correct,
    scorePct,
    bestStreak: result.bestStreak || 0,
    durationMs: result.durationMs || 0,
  };

  const nextHistory = [entry, ...(prevHistory || [])].slice(0, HISTORY_LIMIT);
  return { stats: nextStats, history: nextHistory, entry, scorePct };
}

export async function persistProgress(uid, { stats, history }) {
  if (!uid) return;
  try {
    await setDoc(
      abacusDoc(uid),
      { stats, history, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (_) {
    /* best-effort */
  }
}

export function prettyAuthError(err) {
  const code = err && err.code;
  switch (code) {
    case "auth/invalid-email": return "That email doesn't look right.";
    case "auth/missing-password":
    case "auth/weak-password": return "Password must be at least 6 characters.";
    case "auth/email-already-in-use":
      return "That email is already registered — try signing in instead.";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found": return "Wrong email or password.";
    case "auth/too-many-requests": return "Too many attempts. Wait a moment and retry.";
    case "auth/network-request-failed": return "Network problem. Check your connection.";
    case "auth/operation-not-allowed":
      return "Email/password sign-in isn't enabled yet.";
    default: return (err && err.message) || "Couldn't complete that. Try again.";
  }
}
